import apiClient from './apiClient';
import type {
  AnalysisRun,
  MicroAction,
  MicroActionAnswerPayload,
  ProjectAnalysis,
  ProjectAnalysisSummary,
} from '../types/projectAnalysis';

const PROJECTS_URL = '/projects';

/** Project analysis (lot L2): runs, analyses and micro-actions. */
export const projectAnalysisService = {
  /** 202 with the run; 409 when one is already going; 400 without a deposit. */
  startRun: async (projectId: string, instructions?: string | null): Promise<AnalysisRun> => {
    const body = instructions?.trim() ? { instructions: instructions.trim() } : {};
    const { data } = await apiClient.post<AnalysisRun>(`${PROJECTS_URL}/${projectId}/analysis-runs`, body);
    return data;
  },

  /** `active`: running runs and the ones finished in the last two minutes. */
  listRuns: async (params: { active?: boolean; projectId?: string } = {}): Promise<AnalysisRun[]> => {
    const { data } = await apiClient.get<AnalysisRun[]>(`${PROJECTS_URL}/analysis-runs`, {
      params: {
        ...(params.active ? { active: 1 } : {}),
        ...(params.projectId ? { project_id: params.projectId } : {}),
      },
    });
    return data;
  },

  /** The current analysis, or null when the project was never analysed. */
  current: async (projectId: string): Promise<ProjectAnalysis | null> => {
    const { data } = await apiClient.get<ProjectAnalysis | null>(`${PROJECTS_URL}/${projectId}/analysis`);
    return data ?? null;
  },

  list: async (projectId: string): Promise<ProjectAnalysisSummary[]> => {
    const { data } = await apiClient.get<ProjectAnalysisSummary[]>(`${PROJECTS_URL}/${projectId}/analyses`);
    return data;
  },

  get: async (projectId: string, version: number): Promise<ProjectAnalysis> => {
    const { data } = await apiClient.get<ProjectAnalysis>(`${PROJECTS_URL}/${projectId}/analyses/${version}`);
    return data;
  },

  microActions: async (projectId: string): Promise<MicroAction[]> => {
    const { data } = await apiClient.get<MicroAction[]>(`${PROJECTS_URL}/${projectId}/micro-actions`);
    return data;
  },

  answer: async (microId: string, payload: MicroActionAnswerPayload): Promise<MicroAction> => {
    const { data } = await apiClient.put<MicroAction>(`${PROJECTS_URL}/micro-actions/${microId}/answer`, payload);
    return data;
  },

  clearAnswer: async (microId: string): Promise<MicroAction> => {
    const { data } = await apiClient.delete<MicroAction>(`${PROJECTS_URL}/micro-actions/${microId}/answer`);
    return data;
  },

  resetAnswers: async (projectId: string): Promise<MicroAction[]> => {
    const { data } = await apiClient.delete<MicroAction[]>(`${PROJECTS_URL}/${projectId}/micro-actions/answers`);
    return data;
  },
};

export default projectAnalysisService;
